import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MyDomain } from "../../../App";
import { useParams } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const MONTH_NAMES_FULL = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const NOW = new Date();
const THIS_YEAR = NOW.getFullYear();
const THIS_MON = NOW.getMonth();

const QUICK_PERIODS = [
  {
    label: "This Month",
    getRange: () => ({
      start: new Date(THIS_YEAR, THIS_MON, 1),
      end: new Date(),
    }),
  },
  {
    label: "Last Month",
    getRange: () => ({
      start: new Date(THIS_YEAR, THIS_MON - 1, 1),
      end: new Date(THIS_YEAR, THIS_MON, 0),
    }),
  },
  {
    label: "Last 3M",
    getRange: () => ({
      start: new Date(THIS_YEAR, THIS_MON - 2, 1),
      end: new Date(),
    }),
  },
  {
    label: "Last 6M",
    getRange: () => ({
      start: new Date(THIS_YEAR, THIS_MON - 5, 1),
      end: new Date(),
    }),
  },
  {
    label: "This Year",
    getRange: () => ({ start: new Date(THIS_YEAR, 0, 1), end: new Date() }),
  },
  {
    label: "All Time",
    getRange: () => ({ start: new Date(2000, 0, 1), end: new Date() }),
  },
];

// ── Currency config keyed by ISO code ────────────────────────────────────────
const CURRENCY_CONFIG = {
  ILS: { symbol: "₪", name: "Israeli Shekel", code: "ILS" },
  USD: { symbol: "$", name: "US Dollar", code: "USD" },
  EUR: { symbol: "€", name: "Euro", code: "EUR" },
  GBP: { symbol: "£", name: "British Pound", code: "GBP" },
};

// Get symbol from ISO code (from DB)
function getSymbol(isoCode) {
  return CURRENCY_CONFIG[isoCode]?.symbol || "₪";
}
function getCurrencyName(isoCode) {
  return CURRENCY_CONFIG[isoCode]?.name || isoCode;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function formatDateParam(d) {
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}`;
}
function formatDateDisplay(d) {
  try {
    return d.toLocaleDateString("he-IL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return "";
  }
}
function parseAnyDate(s) {
  if (!s) return null;
  const m1 = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m1) return new Date(+m1[3], +m1[2] - 1, +m1[1]);
  const m2 = s.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (m2) return new Date(+m2[3], +m2[2] - 1, +m2[1]);
  const d = new Date(s);
  return isNaN(d) ? null : d;
}
function formatDate(dateStr = "") {
  const d = parseAnyDate(dateStr);
  if (!d) return dateStr;
  return d.toLocaleDateString("he-IL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}
function getInitials(from = "") {
  const clean = from.replace(/<.*?>/g, "").trim();
  return (
    clean
      .split(" ")
      .map((w) => w[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "??"
  );
}
function getAvatarColor(from = "") {
  const p = [
    { bg: "#e0e7ff", color: "#3730a3" },
    { bg: "#d1fae5", color: "#065f46" },
    { bg: "#fce7f3", color: "#9d174d" },
    { bg: "#fef3c7", color: "#92400e" },
    { bg: "#dbeafe", color: "#1e40af" },
    { bg: "#f3e8ff", color: "#6b21a8" },
  ];
  return p[(from.charCodeAt(0) || 0) % p.length];
}
function getTag(subject = "") {
  const s = subject.toLowerCase();
  if (s.includes("חשבונית") || s.includes("invoice")) return "invoice";
  if (s.includes("فاتورة") || s.includes("إيصال")) return "invoice";
  if (s.includes("receipt") || s.includes("קבלה")) return "receipt";
  return "purchase";
}

// Parse numeric price string — price is now stored as "32.15" (no symbol)
function parsePrice(p = "") {
  if (!p) return 0;
  const clean = String(p).replace(/[^0-9.]/g, "");
  const n = parseFloat(clean);
  return isNaN(n) || n < 0.01 || n > 50000 ? 0 : n;
}

// Format price for display using currency from DB
function formatPrice(price, currency) {
  const amount = parsePrice(price);
  if (!amount) return null;
  const sym = getSymbol(currency || "ILS");
  return `${sym}${amount.toLocaleString("he-IL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Group invoices by currency — uses inv.currency from DB (ISO code)
function groupByCurrency(invoiceList) {
  const groups = {};
  invoiceList.forEach((inv) => {
    if (!inv.price) return;
    const amount = parsePrice(inv.price);
    if (!amount || amount <= 0) return;
    const code = inv.currency || "ILS"; // from DB
    if (!groups[code]) groups[code] = { sum: 0, count: 0 };
    groups[code].sum += amount;
    groups[code].count += 1;
  });
  return groups;
}

const TAG_STYLES = {
  invoice: { bg: "#ede9fe", color: "#5b21b6", border: "#c4b5fd" },
  receipt: { bg: "#d1fae5", color: "#065f46", border: "#6ee7b7" },
  purchase: { bg: "#fef3c7", color: "#92400e", border: "#fcd34d" },
};
const SOURCE_STYLES = {
  gmail: { bg: "#fff1f2", color: "#be123c", border: "#fecdd3", label: "Gmail" },
  outlook: {
    bg: "#eff6ff",
    color: "#1d4ed8",
    border: "#bfdbfe",
    label: "Outlook",
  },
  manual: {
    bg: "#f5f3ff",
    color: "#6d28d9",
    border: "#c4b5fd",
    label: "Manual",
  },
};

const PER_PAGE = 10;
const MONO = "'SF Mono','Fira Code','Consolas',monospace";
const SANS =
  "-apple-system,BlinkMacSystemFont,'Segoe UI','Helvetica Neue',Arial,sans-serif";

const CSS = `
  .inv-root{min-height:100vh;background:#f1f5f9;color:#0f172a;font-family:${SANS};padding:32px}
  .inv-header{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:28px;flex-wrap:wrap;gap:16px}
  .inv-title{font-size:28px;font-weight:700;color:#0f172a;margin:0 0 4px;letter-spacing:-0.4px}
  .inv-subtitle{font-size:13px;color:#64748b;margin:0}
  .inv-providers{display:grid;grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:12px;margin-bottom:20px}
  .inv-provider-card{background:white;border:1px solid #e2e8f0;border-radius:16px;padding:18px 20px;display:flex;align-items:center;justify-content:space-between;gap:12px;transition:border-color 0.2s}
  .inv-provider-card.connected{border-color:#bbf7d0;background:#f0fdf4}
  .inv-provider-left{display:flex;align-items:center;gap:12px}
  .inv-provider-icon{width:40px;height:40px;border-radius:10px;display:flex;align-items:center;justify-content:center;flex-shrink:0}
  .inv-provider-name{font-size:14px;font-weight:600;color:#0f172a;margin-bottom:2px}
  .inv-provider-status{font-size:12px;color:#64748b;display:flex;align-items:center;gap:5px}
  .inv-provider-status.ok{color:#15803d}
  .inv-status-dot{width:6px;height:6px;border-radius:50%;background:#22c55e;animation:inv-pulse 2s infinite}
  @keyframes inv-pulse{0%,100%{box-shadow:0 0 0 2px rgba(34,197,94,0.2)}50%{box-shadow:0 0 0 5px rgba(34,197,94,0.05)}}
  .inv-provider-actions{display:flex;gap:8px;align-items:center;flex-shrink:0}
  .inv-btn-connect-small{display:inline-flex;align-items:center;gap:6px;padding:7px 14px;background:#4f46e5;color:white;border:none;border-radius:8px;font-size:12px;font-weight:500;cursor:pointer;font-family:${SANS};transition:background 0.2s;white-space:nowrap}
  .inv-btn-connect-small:hover{background:#4338ca}
  .inv-btn-disconnect-small{display:inline-flex;align-items:center;gap:5px;padding:6px 12px;background:#fff1f2;color:#be123c;border:1px solid #fecdd3;border-radius:8px;font-size:11px;font-weight:500;cursor:pointer;font-family:${SANS};transition:background 0.2s;white-space:nowrap}
  .inv-btn-disconnect-small:hover{background:#ffe4e6}
  .inv-period-card{background:white;border:1px solid #e2e8f0;border-radius:20px;padding:20px 24px;margin-bottom:16px;box-shadow:0 1px 3px rgba(0,0,0,0.03)}
  .inv-period-card-title{font-size:11px;color:#94a3b8;text-transform:uppercase;letter-spacing:1px;font-weight:600;margin-bottom:14px}
  .inv-quick-pills{display:flex;flex-wrap:wrap;gap:6px;margin-bottom:18px}
  .inv-quick-pill{padding:6px 14px;border-radius:100px;border:1px solid #e2e8f0;background:transparent;color:#64748b;font-size:12px;font-weight:500;cursor:pointer;font-family:${SANS};transition:all 0.15s;white-space:nowrap}
  .inv-quick-pill:hover{border-color:#c7d2fe;color:#4f46e5;background:#eef2ff}
  .inv-quick-pill.active{background:#4f46e5;border-color:#4f46e5;color:white}
  .inv-period-divider{display:flex;align-items:center;gap:12px;margin-bottom:16px}
  .inv-period-divider-line{flex:1;height:1px;background:#f1f5f9}
  .inv-period-divider-text{font-size:11px;color:#94a3b8;white-space:nowrap}
  .inv-month-picker{display:flex;flex-direction:column;gap:10px}
  .inv-year-row{display:flex;gap:6px;margin-bottom:4px}
  .inv-year-btn{padding:4px 12px;border-radius:8px;border:1px solid #e2e8f0;background:transparent;color:#64748b;font-size:12px;font-weight:500;cursor:pointer;font-family:${SANS};transition:all 0.15s}
  .inv-year-btn:hover{border-color:#c7d2fe;color:#4f46e5;background:#eef2ff}
  .inv-year-btn.active{background:#0f172a;border-color:#0f172a;color:white}
  .inv-months-grid{display:grid;grid-template-columns:repeat(6,1fr);gap:6px}
  .inv-month-btn{padding:8px 4px;border-radius:10px;border:1px solid #e2e8f0;background:transparent;color:#64748b;font-size:12px;font-weight:500;cursor:pointer;font-family:${SANS};text-align:center;transition:all 0.15s;position:relative}
  .inv-month-btn:hover{border-color:#c7d2fe;color:#4f46e5;background:#eef2ff}
  .inv-month-btn.selected{background:#4f46e5;border-color:#4f46e5;color:white}
  .inv-month-btn.current::after{content:'';position:absolute;bottom:3px;left:50%;transform:translateX(-50%);width:4px;height:4px;border-radius:50%;background:currentColor;opacity:0.5}
  .inv-month-btn:disabled{opacity:0.3;cursor:not-allowed}
  .inv-period-summary{margin-top:12px;padding:10px 14px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;display:flex;align-items:center;justify-content:space-between;gap:12px}
  .inv-period-summary-text{font-size:13px;color:#334155;font-weight:500}
  .inv-period-summary-sub{font-size:11px;color:#94a3b8;margin-top:2px}
  .inv-btn-clear-months{padding:5px 10px;border-radius:7px;border:1px solid #e2e8f0;background:white;color:#94a3b8;font-size:11px;cursor:pointer;font-family:${SANS};transition:all 0.15s;white-space:nowrap;flex-shrink:0}
  .inv-btn-clear-months:hover{border-color:#fecdd3;color:#be123c;background:#fff1f2}
  .inv-stats-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:12px;margin-bottom:20px}
  .inv-stat-card{background:white;border:1px solid #e2e8f0;border-radius:16px;padding:18px 20px;transition:border-color 0.2s,box-shadow 0.2s}
  .inv-stat-card:hover{border-color:#c7d2fe;box-shadow:0 4px 12px rgba(99,102,241,0.08)}
  .inv-stat-label{font-size:11px;color:#94a3b8;text-transform:uppercase;letter-spacing:0.8px;font-weight:600;margin-bottom:10px}
  .inv-stat-value{font-size:26px;font-weight:700;color:#0f172a;line-height:1;margin-bottom:4px;letter-spacing:-0.5px}
  .inv-stat-sub{font-size:12px;color:#94a3b8}
  .inv-totals-card{background:white;border:1px solid #e2e8f0;border-radius:20px;padding:24px 28px;margin-bottom:16px;box-shadow:0 1px 3px rgba(0,0,0,0.04)}
  .inv-totals-header{font-size:11px;color:#94a3b8;text-transform:uppercase;letter-spacing:1px;font-weight:600;margin-bottom:18px}
  .inv-currency-blocks{display:flex;flex-wrap:wrap;gap:12px}
  .inv-currency-block{flex:1;min-width:160px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:14px;padding:18px 20px}
  .inv-currency-name{font-size:13px;font-weight:600;color:#334155;margin-bottom:2px}
  .inv-currency-code{font-size:11px;color:#94a3b8;font-family:${MONO};margin-bottom:10px}
  .inv-currency-amount{font-size:30px;font-weight:700;color:#0f172a;letter-spacing:-0.8px;line-height:1;margin-bottom:6px}
  .inv-currency-count{font-size:12px;color:#94a3b8}
  .inv-toolbar{background:white;border:1px solid #e2e8f0;border-radius:16px;padding:12px 16px;margin-bottom:16px;display:flex;align-items:center;gap:10px;flex-wrap:wrap;box-shadow:0 1px 3px rgba(0,0,0,0.03)}
  .inv-divider{width:1px;height:24px;background:#e2e8f0;flex-shrink:0}
  .inv-sort-select,.inv-source-select{padding:7px 12px;border-radius:10px;border:1px solid #e2e8f0;background:#f8fafc;color:#334155;font-size:12px;cursor:pointer;font-family:${SANS};outline:none}
  .inv-search-wrap{flex:1;min-width:200px;position:relative}
  .inv-search-input{width:100%;padding:8px 14px 8px 36px;border-radius:10px;border:1px solid #e2e8f0;background:#f8fafc;color:#0f172a;font-size:13px;outline:none;font-family:${SANS};transition:border-color 0.2s,box-shadow 0.2s;box-sizing:border-box}
  .inv-search-input:focus{border-color:#818cf8;box-shadow:0 0 0 3px rgba(129,140,248,0.12);background:white}
  .inv-search-input::placeholder{color:#94a3b8}
  .inv-search-icon{position:absolute;left:10px;top:50%;transform:translateY(-50%)}
  .inv-btn-pull{display:inline-flex;align-items:center;gap:6px;padding:8px 18px;background:#16a34a;color:white;border:none;border-radius:10px;font-size:13px;font-weight:500;cursor:pointer;font-family:${SANS};white-space:nowrap;transition:background 0.2s,transform 0.1s;box-shadow:0 1px 3px rgba(22,163,74,0.25)}
  .inv-btn-pull:hover:not(:disabled){background:#15803d;transform:translateY(-1px)}
  .inv-btn-pull:disabled{background:#e2e8f0;color:#94a3b8;cursor:not-allowed;box-shadow:none}
  .inv-btn-excel{display:inline-flex;align-items:center;gap:6px;padding:8px 14px;background:#f0fdf4;color:#15803d;border:1px solid #bbf7d0;border-radius:10px;font-size:12px;font-weight:500;cursor:pointer;font-family:${SANS};white-space:nowrap;transition:background 0.2s}
  .inv-btn-excel:hover{background:#dcfce7}
  .inv-btn-pdf-report{display:inline-flex;align-items:center;gap:6px;padding:8px 14px;background:#fff7ed;color:#c2410c;border:1px solid #fed7aa;border-radius:10px;font-size:12px;font-weight:500;cursor:pointer;font-family:${SANS};white-space:nowrap;transition:background 0.2s}
  .inv-btn-pdf-report:hover{background:#ffedd5}
  .inv-info-bar{display:flex;align-items:center;gap:8px;padding:10px 14px;background:#f0fdf4;border:1px solid #bbf7d0;border-radius:10px;font-size:12px;color:#15803d;margin-bottom:12px}
  .inv-info-bar.blue{background:#eff6ff;border-color:#bfdbfe;color:#1d4ed8}
  .inv-empty-state{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:64px 24px;background:white;border:1px solid #e2e8f0;border-radius:20px;text-align:center}
  .inv-empty-icon{width:60px;height:60px;border-radius:16px;background:#eef2ff;border:1px solid #c7d2fe;display:flex;align-items:center;justify-content:center;margin-bottom:16px}
  .inv-empty-title{font-size:17px;font-weight:600;color:#0f172a;margin:0 0 8px}
  .inv-empty-sub{font-size:13px;color:#64748b;margin:0;max-width:320px;line-height:1.6}
  .inv-loading-state{display:flex;flex-direction:column;align-items:center;gap:14px;padding:60px 24px;background:white;border:1px solid #e2e8f0;border-radius:20px;color:#64748b;font-size:14px}
  .inv-loading-spinner{width:32px;height:32px;border:2px solid #e2e8f0;border-top-color:#4f46e5;border-radius:50%;animation:inv-spin 0.7s linear infinite}
  @keyframes inv-spin{to{transform:rotate(360deg)}}
  .inv-error{display:flex;align-items:center;justify-content:space-between;padding:12px 16px;background:#fff1f2;border:1px solid #fecdd3;border-radius:12px;font-size:13px;color:#be123c;margin-bottom:20px}
  .inv-results-count{font-size:12px;color:#94a3b8;margin-bottom:10px;padding-left:4px}
  .inv-card{background:white;border:1px solid #e2e8f0;border-radius:16px;padding:16px 20px;margin-bottom:8px;cursor:pointer;transition:border-color 0.15s,box-shadow 0.15s}
  .inv-card:hover{border-color:#c7d2fe;box-shadow:0 2px 8px rgba(99,102,241,0.06)}
  .inv-card.expanded{border-color:#818cf8;box-shadow:0 4px 16px rgba(99,102,241,0.1)}
  .inv-card-top{display:grid;grid-template-columns:42px 1fr auto;gap:14px;align-items:center}
  .inv-avatar{width:42px;height:42px;border-radius:11px;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;flex-shrink:0}
  .inv-card-subject{font-size:14px;font-weight:500;color:#0f172a;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-bottom:3px}
  .inv-card-from{font-size:12px;color:#64748b;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-bottom:7px}
  .inv-badges{display:flex;gap:5px;flex-wrap:wrap;align-items:center}
  .inv-tag{font-size:10px;padding:2px 9px;border-radius:100px;font-weight:600;border:1px solid;letter-spacing:0.3px;text-transform:uppercase}
  .inv-source-badge{font-size:10px;padding:2px 8px;border-radius:5px;font-weight:600;border:1px solid;text-transform:uppercase}
  .inv-inv-num{font-family:${MONO};font-size:10px;padding:2px 8px;border-radius:5px;background:#eef2ff;color:#4f46e5;border:1px solid #c7d2fe}
  .inv-price-badge{font-size:11px;padding:2px 9px;border-radius:100px;background:#f0fdf4;color:#15803d;border:1px solid #bbf7d0;font-weight:600}
  .inv-pdf-badge{font-size:10px;padding:2px 8px;border-radius:5px;background:#fffbeb;color:#b45309;border:1px solid #fde68a;display:inline-flex;align-items:center;gap:3px;text-transform:uppercase;font-weight:600}
  .inv-card-right{text-align:right;flex-shrink:0}
  .inv-card-date{font-size:11px;color:#94a3b8;margin-bottom:5px;font-family:${MONO}}
  .inv-card-price{font-size:17px;font-weight:700;color:#0f172a;letter-spacing:-0.3px}
  .inv-card-toggle{font-size:10px;color:#94a3b8;margin-top:5px}
  .inv-expanded-section{margin-top:14px;padding-top:14px;border-top:1px solid #f1f5f9}
  .inv-expanded-grid{display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-bottom:14px}
  .inv-detail-title{font-size:10px;color:#94a3b8;text-transform:uppercase;letter-spacing:1px;font-weight:600;margin-bottom:8px}
  .inv-detail-text{font-size:13px;color:#475569;line-height:1.6}
  .inv-detail-row{display:flex;justify-content:space-between;font-size:12px;margin-bottom:5px;gap:8px}
  .inv-detail-label{color:#94a3b8}
  .inv-detail-value{color:#334155;font-weight:500;text-align:right}
  .inv-detail-value.mono{font-family:${MONO};color:#4f46e5;font-size:11px}
  .inv-expanded-actions{display:flex;gap:8px;flex-wrap:wrap}
  .inv-btn-download{display:inline-flex;align-items:center;gap:6px;padding:8px 16px;background:#eef2ff;color:#4f46e5;border:1px solid #c7d2fe;border-radius:10px;font-size:12px;font-weight:500;cursor:pointer;font-family:${SANS};transition:background 0.2s}
  .inv-btn-download:hover{background:#e0e7ff}
  .inv-btn-delete{display:inline-flex;align-items:center;gap:6px;padding:8px 14px;background:#fff1f2;color:#be123c;border:1px solid #fecdd3;border-radius:10px;font-size:12px;font-weight:500;cursor:pointer;font-family:${SANS};transition:background 0.2s}
  .inv-btn-delete:hover{background:#ffe4e6}
  .inv-pagination{display:flex;align-items:center;justify-content:center;gap:6px;margin-top:28px;flex-wrap:wrap}
  .inv-page-btn{padding:7px 14px;border-radius:10px;border:1px solid #e2e8f0;background:white;color:#64748b;font-size:12px;cursor:pointer;font-family:${SANS};transition:all 0.15s}
  .inv-page-btn:hover{border-color:#c7d2fe;color:#4f46e5;background:#eef2ff}
  .inv-page-btn.active{background:#4f46e5;border-color:#4f46e5;color:white}
  .inv-page-btn:disabled{opacity:0.35;cursor:not-allowed}
  @media(max-width:640px){
    .inv-root{padding:16px}
    .inv-expanded-grid{grid-template-columns:1fr}
    .inv-card-top{grid-template-columns:36px 1fr}
    .inv-card-right{display:none}
    .inv-currency-amount{font-size:24px}
    .inv-providers{grid-template-columns:1fr}
    .inv-months-grid{grid-template-columns:repeat(4,1fr)}
  }
`;

// ── Map manual invoice → unified format ──────────────────────────────────────
function mapManualInvoice(inv) {
  return {
    subject: inv.vendorName || inv.inoviceNumber || "Manual Invoice",
    from: inv.vendorName || "—",
    date: inv.inoviceDate || "",
    messageId: null,
    price: inv.totalAmount ? String(parseFloat(inv.totalAmount) || 0) : null,
    currency: "ILS", // manual invoices are always ILS
    invoiceNumber: inv.inoviceNumber || null,
    description:
      [inv.businessType, inv.businessNumber].filter(Boolean).join(" · ") ||
      null,
    hasAttachment: false,
    attachmentId: null,
    attachmentName: null,
    score: 10,
    source: "manual",
    dbId: null,
    manualId: inv.id,
    expensesId: inv.expensesId,
  };
}

function filterManualByRange(list, start, end) {
  return list.filter((inv) => {
    const d = parseAnyDate(inv.date);
    if (!d) return false;
    return d >= start && d <= end;
  });
}

export default function BossInvoices() {
  const { t } = useTranslation();
  const [gmailConnected, setGmailConnected] = useState(false);
  const [gmailDisconnecting, setGmailDisconnecting] = useState(false);
  const [outlookConnected, setOutlookConnected] = useState(false);
  const [outlookDisconnecting, setOutlookDisconnecting] = useState(false);

  const [invoices, setInvoices] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [lastPulled, setLastPulled] = useState(null);
  const [hasPulled, setHasPulled] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [sortBy, setSortBy] = useState("date");
  const [downloading, setDownloading] = useState({});
  const [deleting, setDeleting] = useState({});
  const [newlySaved, setNewlySaved] = useState(0);

  const [activeQuick, setActiveQuick] = useState(0);
  const [selectedYear, setSelectedYear] = useState(THIS_YEAR);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [dateRange, setDateRange] = useState(QUICK_PERIODS[0].getRange());

  const { BossId } = useParams();
  const API = MyDomain.endsWith("/") ? MyDomain.slice(0, -1) : MyDomain;
  const user = JSON.parse(localStorage.getItem("user"));
  const userId = user?.id;
  const anyConnected = gmailConnected || outlookConnected;

  useEffect(() => {
    if (selectedMonths.length > 0) {
      const sorted = [...selectedMonths].sort((a, b) => a - b);
      const start = new Date(selectedYear, sorted[0], 1);
      const last = sorted[sorted.length - 1];
      const end = new Date(selectedYear, last + 1, 0, 23, 59, 59);
      const today = new Date();
      setDateRange({ start, end: end > today ? today : end });
      setActiveQuick(null);
    }
  }, [selectedMonths, selectedYear]);

  useEffect(() => {
    if (userId) {
      checkGmailStatus();
      checkOutlookStatus();
    }
    const params = new URLSearchParams(window.location.search);
    if (params.get("gmail") === "connected") setGmailConnected(true);
    if (params.get("outlook") === "connected") setOutlookConnected(true);
    if (
      params.get("gmail") === "connected" ||
      params.get("outlook") === "connected"
    )
      window.history.replaceState(
        {},
        document.title,
        `/boss-main-page/${BossId}/invoices`,
      );
  }, []);

  useEffect(() => {
    if (userId) {
      setInvoices([]);
      setFiltered([]);
      setHasPulled(false);
    }
  }, [dateRange]);

  useEffect(() => {
    const q = search.toLowerCase();
    let result = invoices.filter(
      (inv) =>
        (sourceFilter === "all" || inv.source === sourceFilter) &&
        (inv.subject?.toLowerCase().includes(q) ||
          inv.from?.toLowerCase().includes(q) ||
          inv.description?.toLowerCase().includes(q) ||
          (inv.price && String(inv.price).includes(q)) ||
          inv.invoiceNumber?.toLowerCase().includes(q)),
    );
    if (sortBy === "price")
      result = [...result].sort(
        (a, b) => parsePrice(b.price) - parsePrice(a.price),
      );
    else if (sortBy === "date")
      result = [...result].sort((a, b) => {
        const da = parseAnyDate(a.date),
          db = parseAnyDate(b.date);
        return (db || 0) - (da || 0);
      });
    else if (sortBy === "sender")
      result = [...result].sort((a, b) =>
        (a.from || "").localeCompare(b.from || ""),
      );
    else if (sortBy === "source")
      result = [...result].sort((a, b) =>
        (a.source || "").localeCompare(b.source || ""),
      );
    setFiltered(result);
    setPage(1);
  }, [search, invoices, sortBy, sourceFilter]);

  // ── Currency totals — uses ISO code from DB ──
  const currencyTotals = useMemo(() => groupByCurrency(filtered), [filtered]);

  const checkGmailStatus = async () => {
    try {
      const r = await fetch(`${API}/api/gmail/status/${userId}`);
      const d = await r.json();
      setGmailConnected(d.connected);
    } catch {}
  };
  const checkOutlookStatus = async () => {
    try {
      const r = await fetch(`${API}/api/outlook/status/${userId}`);
      const d = await r.json();
      setOutlookConnected(d.connected);
    } catch {}
  };

  // ── Pull from email APIs ─────────────────────────────────────────────────
  const pullInvoices = async () => {
    if (!anyConnected) return;
    setLoading(true);
    setError(null);
    setExpanded(null);
    setNewlySaved(0);

    const startParam = formatDateParam(dateRange.start);
    const endParam = formatDateParam(dateRange.end);
    const all = [];
    let totalNew = 0;

    if (gmailConnected) {
      try {
        const r = await fetch(
          `${API}/api/gmail/pull-invoices/${userId}?startDate=${startParam}&endDate=${endParam}`,
        );
        if (r.ok) {
          const d = await r.json();
          all.push(
            ...(d.invoices || []).map((i) => ({ ...i, source: "gmail" })),
          );
          totalNew += d.newlySaved || 0;
        }
      } catch (err) {
        setError(`Gmail pull failed: ${err.message}`);
      }
    }
    if (outlookConnected) {
      try {
        const r = await fetch(
          `${API}/api/outlook/pull-invoices/${userId}?startDate=${startParam}&endDate=${endParam}`,
        );
        if (r.ok) {
          const d = await r.json();
          all.push(
            ...(d.invoices || []).map((i) => ({ ...i, source: "outlook" })),
          );
          totalNew += d.newlySaved || 0;
        }
      } catch (err) {
        setError((prev) =>
          prev
            ? `${prev} | Outlook: ${err.message}`
            : `Outlook pull failed: ${err.message}`,
        );
      }
    }

    const allData = JSON.parse(localStorage.getItem("allData") || "{}");
    const manualRaw = (allData.myInvoices || []).map(mapManualInvoice);
    const manualFiltered = filterManualByRange(
      manualRaw,
      dateRange.start,
      dateRange.end,
    );
    all.push(...manualFiltered);

    all.sort((a, b) => {
      const da = parseAnyDate(a.date),
        db = parseAnyDate(b.date);
      return (db || 0) - (da || 0);
    });
    setInvoices(all);
    setFiltered(all);
    setLastPulled(new Date());
    setHasPulled(true);
    setNewlySaved(totalNew);
    setPage(1);
    setLoading(false);
  };

  const handleQuickPill = (idx) => {
    setActiveQuick(idx);
    setSelectedMonths([]);
    setDateRange(QUICK_PERIODS[idx].getRange());
  };
  const toggleMonth = (mIdx) =>
    setSelectedMonths((prev) =>
      prev.includes(mIdx) ? prev.filter((m) => m !== mIdx) : [...prev, mIdx],
    );
  const clearMonths = () => {
    setSelectedMonths([]);
    setActiveQuick(0);
    setDateRange(QUICK_PERIODS[0].getRange());
  };

  const periodLabel = useMemo(() => {
    if (selectedMonths.length > 0) {
      const sorted = [...selectedMonths].sort((a, b) => a - b);
      return `${sorted.map((m) => MONTH_NAMES[m]).join(" + ")} ${selectedYear}`;
    }
    return activeQuick !== null ? QUICK_PERIODS[activeQuick].label : "Custom";
  }, [selectedMonths, selectedYear, activeQuick]);

  const connectGmail = async () => {
    try {
      const r = await fetch(`${API}/api/gmail/connect/${userId}`);
      const d = await r.json();
      window.location.href = d.url;
    } catch (err) {
      setError(`Gmail connect failed: ${err.message}`);
    }
  };
  const connectOutlook = async () => {
    try {
      const r = await fetch(`${API}/api/outlook/connect/${userId}`);
      const d = await r.json();
      window.location.href = d.url;
    } catch (err) {
      setError(`Outlook connect failed: ${err.message}`);
    }
  };
  const disconnectGmail = async () => {
    if (!window.confirm("Disconnect Gmail?")) return;
    setGmailDisconnecting(true);
    try {
      await fetch(`${API}/api/gmail/disconnect/${userId}`, {
        method: "DELETE",
      });
      setGmailConnected(false);
      setInvoices((prev) => prev.filter((i) => i.source !== "gmail"));
    } catch (err) {
      setError(`Gmail disconnect failed: ${err.message}`);
    }
    setGmailDisconnecting(false);
  };
  const disconnectOutlook = async () => {
    if (!window.confirm("Disconnect Outlook?")) return;
    setOutlookDisconnecting(true);
    try {
      await fetch(`${API}/api/outlook/disconnect/${userId}`, {
        method: "DELETE",
      });
      setOutlookConnected(false);
      setInvoices((prev) => prev.filter((i) => i.source !== "outlook"));
    } catch (err) {
      setError(`Outlook disconnect failed: ${err.message}`);
    }
    setOutlookDisconnecting(false);
  };

  const downloadAttachment = async (inv) => {
    const key = inv.messageId;
    setDownloading((p) => ({ ...p, [key]: true }));
    try {
      const endpoint = inv.source === "outlook" ? "outlook" : "gmail";
      const r = await fetch(
        `${API}/api/${endpoint}/download-attachment/${userId}/${inv.messageId}/${inv.attachmentId}`,
      );
      if (!r.ok) throw new Error("Download failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = inv.attachmentName || "invoice.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(`Download failed: ${err.message}`);
    }
    setDownloading((p) => ({ ...p, [key]: false }));
  };

  const deleteEmailInvoice = async (inv) => {
    if (!window.confirm("Remove this invoice?")) return;
    const key = inv.dbId;
    setDeleting((p) => ({ ...p, [key]: true }));
    try {
      const r = await fetch(`${API}/api/email-invoices/${userId}/${inv.dbId}`, {
        method: "DELETE",
      });
      if (!r.ok) throw new Error("Delete failed");
      setInvoices((prev) => prev.filter((i) => i.dbId !== key));
    } catch (err) {
      setError(`Delete failed: ${err.message}`);
    }
    setDeleting((p) => ({ ...p, [key]: false }));
  };

  const exportExcel = () => {
    const rows = filtered.map((inv) => ({
      Source:
        inv.source === "outlook"
          ? "Outlook"
          : inv.source === "manual"
            ? "Manual"
            : "Gmail",
      Subject: inv.subject || "",
      From: inv.from?.split("<")[0]?.trim() || "",
      Date: formatDate(inv.date),
      "Invoice #": inv.invoiceNumber || "",
      Amount: parsePrice(inv.price) || "",
      Currency: inv.currency || "ILS",
      Description: inv.description || "",
      "Has PDF": inv.hasAttachment ? "Yes" : "No",
    }));
    rows.push({});
    rows.push({ Source: "── SUMMARY ──" });
    Object.entries(currencyTotals).forEach(([code, { sum, count }]) => {
      rows.push({
        Source: `Total (${code})`,
        Subject: `${getSymbol(code)}${sum.toLocaleString("he-IL", { minimumFractionDigits: 2 })}`,
        Amount: parseFloat(sum.toFixed(2)),
        Currency: code,
        From: `${count} invoices`,
      });
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Invoices");
    ws["!cols"] = [
      { wch: 10 },
      { wch: 50 },
      { wch: 30 },
      { wch: 14 },
      { wch: 20 },
      { wch: 12 },
      { wch: 8 },
      { wch: 55 },
      { wch: 8 },
    ];
    XLSX.writeFile(
      wb,
      `invoices_${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
  };

  const exportPDF = () => {
    const html = `<!DOCTYPE html><html dir="rtl"><head><meta charset="utf-8"><title>Invoice Report</title>
<style>
  @media print{@page{size:A4 landscape;margin:15mm}body{margin:0}}
  body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;padding:20px;direction:rtl;background:white;color:#0f172a}
  h1{color:#4f46e5;text-align:center;font-size:24px;margin-bottom:6px;font-weight:700}
  .sub{text-align:center;color:#64748b;font-size:13px;margin-bottom:20px}
  table{width:100%;border-collapse:collapse;font-size:9px}
  th{background:#4f46e5;color:white;padding:7px 6px;text-align:right;font-weight:600}
  td{padding:6px;text-align:right;border-bottom:1px solid #f1f5f9;color:#334155}
  tr:nth-child(even) td{background:#f8fafc}
  .price{font-weight:700;color:#15803d}.num{font-family:monospace;color:#4f46e5;font-size:8px}
  .src-gmail{background:#fff1f2;color:#be123c;padding:1px 5px;border-radius:3px;font-size:8px;font-weight:700}
  .src-outlook{background:#eff6ff;color:#1d4ed8;padding:1px 5px;border-radius:3px;font-size:8px;font-weight:700}
  .src-manual{background:#f5f3ff;color:#6d28d9;padding:1px 5px;border-radius:3px;font-size:8px;font-weight:700}
  .tag{padding:1px 6px;border-radius:20px;font-size:8px;font-weight:700}
  .tag-invoice{background:#ede9fe;color:#5b21b6}.tag-receipt{background:#d1fae5;color:#065f46}.tag-purchase{background:#fef3c7;color:#92400e}
  .footer{margin-top:16px;text-align:center;color:#94a3b8;font-size:10px}
</style></head><body>
<h1>Invoice Report</h1>
<div class="sub">Period: ${periodLabel} &nbsp;·&nbsp; ${formatDateDisplay(dateRange.start)} – ${formatDateDisplay(dateRange.end)} &nbsp;·&nbsp; Generated: ${new Date().toLocaleDateString("he-IL")} &nbsp;·&nbsp; ${filtered.length} invoices</div>
<table><thead><tr>
  <th style="width:4%">Src</th><th style="width:27%">Subject</th><th style="width:16%">From</th>
  <th style="width:9%">Date</th><th style="width:12%">Invoice #</th><th style="width:9%">Amount</th>
  <th style="width:5%">Curr</th><th style="width:6%">Type</th><th style="width:5%">PDF</th>
</tr></thead><tbody>${filtered
      .map((inv, i) => {
        const tag = getTag(inv.subject || "");
        const srcLabel =
          inv.source === "outlook"
            ? "OL"
            : inv.source === "manual"
              ? "MN"
              : "GM";
        const displayPrice = formatPrice(inv.price, inv.currency) || "—";
        return `<tr>
    <td><span class="src-${inv.source || "gmail"}">${srcLabel}</span></td>
    <td>${inv.subject || "—"}</td><td>${inv.from?.split("<")[0]?.trim() || "—"}</td>
    <td>${formatDate(inv.date)}</td>
    <td class="num">${inv.invoiceNumber ? `#${inv.invoiceNumber}` : "—"}</td>
    <td class="price">${displayPrice}</td>
    <td>${inv.currency || "ILS"}</td>
    <td><span class="tag tag-${tag}">${tag}</span></td>
    <td>${inv.hasAttachment ? "✓" : "—"}</td>
  </tr>`;
      })
      .join("")}</tbody></table>
<div class="footer">Generated by NeholEsek · ${new Date().toLocaleString("he-IL")}</div>
<script>window.onload=function(){window.print();setTimeout(function(){window.close();},500);};</script>
</body></html>`;
    const w = window.open("", "_blank");
    w.document.write(html);
    w.document.close();
  };

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const withPrice = invoices.filter(
    (inv) => inv.price && parsePrice(inv.price) > 0,
  ).length;
  const withPDF = invoices.filter((inv) => inv.hasAttachment).length;
  const withInvNum = invoices.filter((inv) => inv.invoiceNumber).length;
  const gmailCount = invoices.filter((inv) => inv.source === "gmail").length;
  const outlookCount = invoices.filter(
    (inv) => inv.source === "outlook",
  ).length;
  const manualCount = invoices.filter((inv) => inv.source === "manual").length;
  const availableYears = [THIS_YEAR, THIS_YEAR - 1, THIS_YEAR - 2];

  return (
    <>
      <style>{CSS}</style>
      <div className="inv-root">
        <div className="inv-header">
          <div>
            <h1 className="inv-title">{t("invoices")}</h1>
            <p className="inv-subtitle">{t("fromInvoices")}</p>
          </div>
        </div>

        {error && (
          <div className="inv-error">
            <span>⚠ {error}</span>
            <button
              onClick={() => setError(null)}
              style={{
                background: "none",
                border: "none",
                color: "#be123c",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              ✕
            </button>
          </div>
        )}

        {/* Deduplication notice */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "10px",
            background: "#f0f9ff",
            border: "1px solid #bae6fd",
            borderRadius: "10px",
            padding: "12px 16px",
            marginBottom: "18px",
            color: "#0369a1",
            fontSize: "13.5px",
            lineHeight: "1.55",
          }}
        >
          <span style={{ fontSize: "17px", marginTop: "1px", flexShrink: 0 }}>ℹ️</span>
          <span>
            <strong>No duplicates will be saved.</strong> When you pull from your email, you may see invoices or receipts that were already pulled before — this is normal. However, any document that was previously imported will <strong>not be saved again</strong>. Your records will always remain clean and duplicate-free, even if you pull multiple times.
          </span>
        </div>

        {/* Provider cards */}
        <div className="inv-providers">
          {/* Gmail */}
          <div
            className={`inv-provider-card${gmailConnected ? " connected" : ""}`}
          >
            <div className="inv-provider-left">
              <div
                className="inv-provider-icon"
                style={{ background: "#fff1f2" }}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                    fill="#fee2e2"
                    stroke="#fca5a5"
                    strokeWidth="1.5"
                  />
                  <polyline
                    points="22,6 12,13 2,6"
                    stroke="#ef4444"
                    strokeWidth="1.5"
                    fill="none"
                  />
                </svg>
              </div>
              <div>
                <div className="inv-provider-name">Gmail</div>
                <div
                  className={`inv-provider-status${gmailConnected ? " ok" : ""}`}
                >
                  {gmailConnected ? (
                    <>
                      <span className="inv-status-dot" /> {t("success")} ·{" "}
                      {gmailCount} {t("invoices")}
                    </>
                  ) : (
                    t("noInvoicesFound")
                  )}
                </div>
              </div>
            </div>
            <div className="inv-provider-actions">
              {gmailConnected ? (
                <button
                  className="inv-btn-disconnect-small"
                  onClick={disconnectGmail}
                  disabled={gmailDisconnecting}
                >
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M18.36 6.64a9 9 0 11-12.73 0" />
                    <line x1="12" y1="2" x2="12" y2="12" />
                  </svg>
                  {gmailDisconnecting ? "..." : "Disconnect"}
                </button>
              ) : (
                <button
                  className="inv-btn-connect-small"
                  onClick={connectGmail}
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                  Connect Gmail
                </button>
              )}
            </div>
          </div>

          {/* Outlook */}
          <div
            className={`inv-provider-card${outlookConnected ? " connected" : ""}`}
          >
            <div className="inv-provider-left">
              <div
                className="inv-provider-icon"
                style={{ background: "#eff6ff" }}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <rect
                    x="2"
                    y="4"
                    width="20"
                    height="16"
                    rx="2"
                    fill="#dbeafe"
                    stroke="#93c5fd"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M8 8h8M8 12h6"
                    stroke="#3b82f6"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <circle cx="7" cy="16" r="2" fill="#3b82f6" />
                </svg>
              </div>
              <div>
                <div className="inv-provider-name">Outlook</div>
                <div
                  className={`inv-provider-status${outlookConnected ? " ok" : ""}`}
                >
                  {outlookConnected ? (
                    <>
                      <span className="inv-status-dot" /> {t("success")} ·{" "}
                      {outlookCount} {t("invoices")}
                    </>
                  ) : (
                    t("noInvoicesFound")
                  )}
                </div>
              </div>
            </div>
            <div className="inv-provider-actions">
              {outlookConnected ? (
                <button
                  className="inv-btn-disconnect-small"
                  onClick={disconnectOutlook}
                  disabled={outlookDisconnecting}
                >
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M18.36 6.64a9 9 0 11-12.73 0" />
                    <line x1="12" y1="2" x2="12" y2="12" />
                  </svg>
                  {outlookDisconnecting ? "..." : "Disconnect"}
                </button>
              ) : (
                <button
                  className="inv-btn-connect-small"
                  onClick={connectOutlook}
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="M8 8h8M8 12h6" strokeLinecap="round" />
                  </svg>
                  Connect Outlook
                </button>
              )}
            </div>
          </div>

          {/* Manual */}
          <div
            className="inv-provider-card connected"
            style={{ borderColor: "#c4b5fd", background: "#f5f3ff" }}
          >
            <div className="inv-provider-left">
              <div
                className="inv-provider-icon"
                style={{ background: "#ede9fe" }}
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#7c3aed"
                  strokeWidth="1.5"
                >
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
              </div>
              <div>
                <div className="inv-provider-name">{t("invoices")}</div>
                <div className="inv-provider-status ok">
                  <span
                    className="inv-status-dot"
                    style={{ background: "#7c3aed" }}
                  />{" "}
                  {manualCount} {t("invoices")}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Period selector */}
        <div className="inv-period-card">
          <div className="inv-period-card-title">{t("invoiceDate")}</div>
          <div className="inv-quick-pills">
            {QUICK_PERIODS.map((qp, idx) => (
              <button
                key={idx}
                className={`inv-quick-pill${activeQuick === idx ? " active" : ""}`}
                onClick={() => handleQuickPill(idx)}
              >
                {qp.label}
              </button>
            ))}
          </div>
          <div className="inv-period-divider">
            <div className="inv-period-divider-line" />
            <span className="inv-period-divider-text">{t("date")}</span>
            <div className="inv-period-divider-line" />
          </div>
          <div className="inv-month-picker">
            <div className="inv-year-row">
              {availableYears.map((y) => (
                <button
                  key={y}
                  className={`inv-year-btn${selectedYear === y ? " active" : ""}`}
                  onClick={() => setSelectedYear(y)}
                >
                  {y}
                </button>
              ))}
            </div>
            <div className="inv-months-grid">
              {MONTH_NAMES.map((name, mIdx) => {
                const isFuture = selectedYear === THIS_YEAR && mIdx > THIS_MON;
                const isCurrent =
                  selectedYear === THIS_YEAR && mIdx === THIS_MON;
                const isSelected = selectedMonths.includes(mIdx);
                return (
                  <button
                    key={mIdx}
                    className={`inv-month-btn${isSelected ? " selected" : ""}${isCurrent ? " current" : ""}`}
                    onClick={() => !isFuture && toggleMonth(mIdx)}
                    disabled={isFuture}
                    title={MONTH_NAMES_FULL[mIdx]}
                  >
                    {name}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="inv-period-summary">
            <div>
              <div className="inv-period-summary-text">{periodLabel}</div>
              <div className="inv-period-summary-sub">
                {formatDateDisplay(dateRange.start)} –{" "}
                {formatDateDisplay(dateRange.end)}
              </div>
            </div>
            {selectedMonths.length > 0 && (
              <button className="inv-btn-clear-months" onClick={clearMonths}>
                {t("deselectAll")}
              </button>
            )}
          </div>
        </div>

        {newlySaved > 0 && (
          <div className="inv-info-bar">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            {newlySaved} {t("invoices")} {t("success")}
          </div>
        )}

        {/* Stats */}
        <div className="inv-stats-grid">
          {[
            {
              label: t("totalInvoices"),
              value: invoices.length,
              sub: "all sources",
            },
            { label: t("amount"), value: withPrice, sub: "extracted" },
            { label: t("invoiceNumber"), value: withInvNum, sub: "identified" },
            { label: "PDF files", value: withPDF, sub: "attachments" },
            {
              label: "Last synced",
              value: lastPulled
                ? lastPulled.toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "—",
              sub: lastPulled ? "today" : "never",
            },
          ].map((s, i) => (
            <div key={i} className="inv-stat-card">
              <div className="inv-stat-label">{s.label}</div>
              <div
                className="inv-stat-value"
                style={{ fontSize: i === 4 ? "18px" : "26px" }}
              >
                {s.value}
              </div>
              <div className="inv-stat-sub">{s.sub}</div>
            </div>
          ))}
        </div>

        {/* ── Currency totals — grouped by ISO code from DB ── */}
        {Object.keys(currencyTotals).length > 0 && (
          <div className="inv-totals-card">
            <div className="inv-totals-header">
              {t("totals")} · {periodLabel}
            </div>
            <div className="inv-currency-blocks">
              {Object.entries(currencyTotals).map(([code, { sum, count }]) => (
                <div key={code} className="inv-currency-block">
                  <div className="inv-currency-name">
                    {getCurrencyName(code)}
                  </div>
                  <div className="inv-currency-code">{code}</div>
                  <div className="inv-currency-amount">
                    {getSymbol(code)}
                    {sum.toLocaleString("he-IL", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </div>
                  <div className="inv-currency-count">
                    {count} {t("invoices")}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Toolbar */}
        <div className="inv-toolbar">
          <select
            className="inv-sort-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="date">Sort: Date</option>
            <option value="price">Sort: Price</option>
            <option value="sender">Sort: Sender</option>
            <option value="source">Sort: Source</option>
          </select>
          <select
            className="inv-source-select"
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
          >
            <option value="all">All sources</option>
            <option value="gmail">Gmail only</option>
            <option value="outlook">Outlook only</option>
            <option value="manual">Manual only</option>
          </select>
          <div className="inv-divider" />
          <div className="inv-search-wrap">
            <svg
              className="inv-search-icon"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#94a3b8"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              className="inv-search-input"
              type="text"
              placeholder={t("searchInvoices")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {(gmailConnected || outlookConnected) && (
            <button
              className="inv-btn-pull"
              onClick={pullInvoices}
              disabled={loading}
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <polyline points="23 4 23 10 17 10" />
                <path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
              </svg>
              {loading ? t("loading") : "Pull Emails"}
            </button>
          )}
          {hasPulled && filtered.length > 0 && (
            <>
              <button className="inv-btn-excel" onClick={exportExcel}>
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                Excel
              </button>
              <button className="inv-btn-pdf-report" onClick={exportPDF}>
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                PDF Report
              </button>
            </>
          )}
        </div>

        {!hasPulled && !loading && (
          <div className="inv-empty-state">
            <div className="inv-empty-icon">
              <svg
                width="26"
                height="26"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6366f1"
                strokeWidth="1.5"
              >
                <polyline points="23 4 23 10 17 10" />
                <path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
              </svg>
            </div>
            <h2 className="inv-empty-title">{t("invoicesWillAppearHere")}</h2>
            <p className="inv-empty-sub">{t("createNewInvoice")}</p>
          </div>
        )}
        {loading && (
          <div className="inv-loading-state">
            <div className="inv-loading-spinner" />
            Fetching emails from{" "}
            {[gmailConnected && "Gmail", outlookConnected && "Outlook"]
              .filter(Boolean)
              .join(" & ")}
            ...
          </div>
        )}
        {hasPulled && !loading && filtered.length === 0 && (
          <div className="inv-empty-state">
            <div className="inv-empty-icon">
              <svg
                width="26"
                height="26"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6366f1"
                strokeWidth="1.5"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </div>
            <h2 className="inv-empty-title">{t("noInvoicesFound")}</h2>
            <p className="inv-empty-sub">{t("searchInvoices")}</p>
          </div>
        )}

        {!loading && paginated.length > 0 && (
          <div className="inv-results-count">
            {t("outOf")} {(page - 1) * PER_PAGE + 1}–
            {Math.min(page * PER_PAGE, filtered.length)} / {filtered.length}
          </div>
        )}

        {/* Invoice cards */}
        {!loading &&
          paginated.map((inv, i) => {
            const av = getAvatarColor(inv.from || "");
            const tag = getTag(inv.subject || "");
            const ts = TAG_STYLES[tag] || TAG_STYLES.purchase;
            const ss = SOURCE_STYLES[inv.source] || SOURCE_STYLES.gmail;
            const isExpanded = expanded === i;
            // Display price with correct symbol from DB currency
            const displayPrice = formatPrice(inv.price, inv.currency);

            return (
              <div
                key={i}
                className={`inv-card${isExpanded ? " expanded" : ""}`}
                onClick={() => setExpanded(isExpanded ? null : i)}
              >
                <div className="inv-card-top">
                  <div
                    className="inv-avatar"
                    style={{ background: av.bg, color: av.color }}
                  >
                    {getInitials(inv.from || "")}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div className="inv-card-subject">
                      {inv.subject || "No subject"}
                    </div>
                    <div className="inv-card-from">
                      {inv.from || "Unknown sender"}
                    </div>
                    <div className="inv-badges">
                      <span
                        className="inv-source-badge"
                        style={{
                          background: ss.bg,
                          color: ss.color,
                          borderColor: ss.border,
                        }}
                      >
                        {ss.label}
                      </span>
                      <span
                        className="inv-tag"
                        style={{
                          background: ts.bg,
                          color: ts.color,
                          borderColor: ts.border,
                        }}
                      >
                        {tag}
                      </span>
                      {inv.invoiceNumber && (
                        <span className="inv-inv-num">
                          #{inv.invoiceNumber}
                        </span>
                      )}
                      {displayPrice && (
                        <span className="inv-price-badge">{displayPrice}</span>
                      )}
                      {inv.hasAttachment && (
                        <span className="inv-pdf-badge">
                          <svg
                            width="9"
                            height="9"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                          >
                            <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                          </svg>
                          PDF
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="inv-card-right">
                    <div className="inv-card-date">{formatDate(inv.date)}</div>
                    {displayPrice && (
                      <div className="inv-card-price">{displayPrice}</div>
                    )}
                    <div className="inv-card-toggle">
                      {isExpanded ? "▲ less" : "▼ details"}
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div
                    className="inv-expanded-section"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="inv-expanded-grid">
                      <div>
                        <div className="inv-detail-title">{t("note")}</div>
                        <div className="inv-detail-text">
                          {inv.description || t("noData")}
                        </div>
                      </div>
                      <div>
                        <div className="inv-detail-title">
                          {t("invoiceDetails")}
                        </div>
                        {[
                          { label: t("vendorName"), value: ss.label },
                          {
                            label: t("vendorName"),
                            value: inv.from?.split("<")[0]?.trim() || "—",
                          },
                          { label: t("date"), value: formatDate(inv.date) },
                          {
                            label: t("invoiceNumber"),
                            value: inv.invoiceNumber
                              ? `#${inv.invoiceNumber}`
                              : "—",
                            mono: !!inv.invoiceNumber,
                          },
                          {
                            label: t("amount"),
                            value: displayPrice || t("noData"),
                          },
                          {
                            label: t("totalAmount"),
                            value:
                              getCurrencyName(inv.currency || "ILS") +
                              " (" +
                              (inv.currency || "ILS") +
                              ")",
                          },
                          {
                            label: t("items"),
                            value: inv.hasAttachment ? t("yes") : t("no"),
                          },
                        ].map((row) => (
                          <div key={row.label} className="inv-detail-row">
                            <span className="inv-detail-label">
                              {row.label}
                            </span>
                            <span
                              className={`inv-detail-value${row.mono ? " mono" : ""}`}
                            >
                              {row.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="inv-expanded-actions">
                      {inv.hasAttachment && inv.attachmentId && (
                        <button
                          className="inv-btn-download"
                          onClick={() => downloadAttachment(inv)}
                        >
                          {downloading[inv.messageId] ? (
                            "⏳ Downloading..."
                          ) : (
                            <>
                              <svg
                                width="13"
                                height="13"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                                <polyline points="7 10 12 15 17 10" />
                                <line x1="12" y1="15" x2="12" y2="3" />
                              </svg>
                              Download PDF ·{" "}
                              {inv.attachmentName || "invoice.pdf"}
                            </>
                          )}
                        </button>
                      )}
                      {inv.dbId && inv.source !== "manual" && (
                        <button
                          className="inv-btn-delete"
                          onClick={() => deleteEmailInvoice(inv)}
                        >
                          {deleting[inv.dbId] ? (
                            "⏳ Removing..."
                          ) : (
                            <>
                              <svg
                                width="13"
                                height="13"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6l-1 14H6L5 6" />
                                <path d="M10 11v6M14 11v6" />
                                <path d="M9 6V4h6v2" />
                              </svg>
                              Remove from saved
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

        {totalPages > 1 && (
          <div className="inv-pagination">
            <button
              className="inv-page-btn"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              ← Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                className={`inv-page-btn${page === i + 1 ? " active" : ""}`}
                onClick={() => setPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
            <button
              className="inv-page-btn"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </>
  );
}
